import React, { useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { push } from 'connected-react-router';

import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import { Typography, Card } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import WorkIcon from '@material-ui/icons/Work';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';

import { Stack } from 'components/layout';
import { setSearchState, setChannelWorkflowGridStateUpdate } from 'store/actions';
import { Constants } from 'utilities/constants';
import { OpportunityStatusChip } from 'components/layout';

export const highlightSearchString = (text, searchString, classes) => {
  let oneSpaceSearchString = searchString.replace(/  +/g, ' ');//remove excess spacing
  if (text?.length > 0 && oneSpaceSearchString?.length > 0 && text.toUpperCase().indexOf(oneSpaceSearchString.toUpperCase()) !== -1) {
    let words = text.toUpperCase().split(oneSpaceSearchString.toUpperCase());
    let newArray = [];
    let newLocation = 0;
    for (let i = 0; i < words.length; i++) {
      let currentString = text.substring(newLocation, newLocation + words[i].length);
      let currentSeparator = ""
      if (newLocation + words[i].length + oneSpaceSearchString.length <= text.length) {
        currentSeparator = text.substring(newLocation + words[i].length, newLocation + words[i].length + oneSpaceSearchString.length);//preserve casing of separator
      }
      newLocation = newLocation + oneSpaceSearchString.length + words[i].length;
      newArray.push({ normalWord: currentString });
      newArray.push({ boldedWord: currentSeparator });
    }
    return (
      newArray.map((word, i) => {
        if (word.normalWord) {
          return (
            <Typography className={classes.inlineText} key={i}>
              {word.normalWord}
            </Typography>
          )
        } else {
          return (
            <Typography className={classes.inlineSearchText} key={i}>
              {word.boldedWord}
            </Typography>
          )
        }
      })
    )
  } else {
    return (
      <Typography className={classes.inlineText}>
        {text}
      </Typography>
    )
  }
}

const displayId = (resultType, id, searchString, classes) => {
  if (resultType === 'property' && id) {
    if (id.toString() === searchString) {
      return (
        <Typography className={classes.inlineSearchText}>
          {id}
        </Typography>
      )
    } else {
      return (
        <Typography className={classes.inlineText}>
          {id}
        </Typography>
      )
    }
  }
  return null;
}

const getOppStatusInformation = (resultType, opportunityStatus, underwriteStatus, userId) => {
  if (resultType === 'property' && opportunityStatus) {
    let txnOpp = {
      opportunityStatus: opportunityStatus,
      underwriteStatus: underwriteStatus,
      userId: userId
    }
    return (
      <Typography component="div">
        <OpportunityStatusChip txnOpp={txnOpp} />
      </Typography>
    )
  }
  return null;
}
const getIcon = resultType => {
  switch (resultType) {
    case 'property':
      return <HomeIcon/>;
    case 'auction':
      return <WorkIcon/>;
    case 'import':
      return <CreateNewFolderIcon/>;
    default:
      return null;
  }
}

const getAuditInformation = (resultType, importedBy, importedOn, latestPricingDateTime) => {
  switch (resultType) {
    case 'property':
      // if there is a latestPricingDateTime, the search result was from the user clicking My Last 100 Priced button
      if (latestPricingDateTime) {
        return (
          <Typography>
            {`Last valuated on ${latestPricingDateTime?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
          </Typography>
        )
      } else {
        // importedOn is the TransactionOpportunity.CreatedOn datetime, the original date that the txnOpp was created when it was imported.
        // It differs from Import.CreatedOn and Import.CompletedOn, which more accurately represent Latest Imported On
        return (
          <Typography>
            {`Initially imported on ${importedOn?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}${importedBy ? ` by ${importedBy}` : ''}`}
          </Typography>
        )
      }
    case 'auction':
      return (
        <Typography>
          {`Imported on ${importedOn?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}${importedBy ? ` by ${importedBy}` : ''}`}
        </Typography>
      )
    case 'import':
      return (
        <Typography>
          {`Imported on ${importedOn?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}${importedBy ? ` by ${importedBy}` : ''}`}
        </Typography>
      )
    default:
      return null;
  }
}

const getPortfolioInformation = (resultType, portfolioName, classes) => {
  if (resultType === 'property' && portfolioName) {
    return (
      <Typography component="div">
        {`${portfolioName}`}<WorkIcon className={classes.inlineIcon}/>
      </Typography>
    )
  }
  return null;
}

const SearchCard = ({ classes, resultType, title, data, importedBy, importedOn, portfolioName, id, channel, searchString, push,
  setSearchState, setChannelWorkflowGridStateUpdate, opportunityStatus, underwriteStatus, userId, latestPricingDateTime }) => {
  const titleDisplay = useMemo(() => {
    return highlightSearchString(title, searchString, classes);
  }, [title, searchString]);

  const dataDisplay = useMemo(() => {
    return data.map((str, i) => {
      return (
        <Typography key={i}>
          {str}
        </Typography>
      );
    });
  }, [data]);

  const idDisplay = useMemo(() => {
    return displayId(resultType, id, searchString, classes);
  }, [id, searchString]);

  const iconDisplay = useMemo(() => {
    return getIcon(resultType);
  }, []);

  const auditDisplay = useMemo(() => {
    return getAuditInformation(resultType, importedBy, importedOn, latestPricingDateTime);
  }, []);

  const portfolioDisplay = useMemo(() => {
    return getPortfolioInformation(resultType, portfolioName, classes);
  }, []);
  const oppStatusChipDisplay = useMemo(() => {
    return getOppStatusInformation(resultType, opportunityStatus, underwriteStatus, userId);
  }, []);
  const onClick = e => {
    switch (resultType) {
      case 'property':
        push(`${Constants.Routes.UNDERWRITE_PROPERTY}/${id}`)
        break;
      case 'auction':
        push(`${Constants.Routes.UNDERWRITE_AUCTION}/${id}`)
        break;
      case 'import':
        setChannelWorkflowGridStateUpdate({ filterValue: [['importId', '=', id]], columnMods: [{ field: 'id', type: 'sortOrder', value: 'asc' }] }, true);
        push(Constants.Routes.CHANNEL_WORKFLOW);
        break;
      default:
        break;
    }
    setSearchState({ searchIsOpen: false });
  }

  return (
    <Card className={classes.searchCard} onClick={onClick}>
      <Stack horizontal>
        <div className={classes.cardSection}>
          {iconDisplay}
        </div>
        <div className={classNames(classes.cardMainContent, classes.cardSection)}>
          {titleDisplay}
          {dataDisplay}
          {auditDisplay}
          {portfolioDisplay}
        </div>
        <div className={classes.cardSection} style={{ display: 'flex', flexDirection: 'column' }}>
          {idDisplay}
          {channel &&
            <Typography>
              {channel}
            </Typography>
          }
          {oppStatusChipDisplay}
        </div>
      </Stack>
    </Card>
  )
}

const mapStateToProps = state => ({
  
});

const mapDispatchToProps = dispatch => ({
  push: route => dispatch(push(route)),
  setChannelWorkflowGridStateUpdate: (state, isUpdate) => dispatch(setChannelWorkflowGridStateUpdate(state, isUpdate)),
  setSearchState: state => dispatch(setSearchState(state)),
});

const styles = theme => ({
  searchCard: {
    margin: '10px 20px',
    cursor: 'pointer',
    "&:hover": {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    }
  },
  cardMainContent: {
    flexGrow: 1,
    padding: 10
  },
  cardSection: {
    padding: 10
  },
  inlineText: {
    display: 'inline-block',
    whiteSpace: 'pre'
  },
  inlineSearchText: {
    display: 'inline-block',
    whiteSpace: 'pre',
    fontWeight: 'bold',
    backgroundColor: 'yellow'
  },
  inlineIcon: {
    verticalAlign: 'bottom',
    margin: '0 3px'
  }
});

export default compose(
  withRouter,
  withStyles(styles),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(SearchCard);
